import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
    getUserDeck,
    getGameIcon,
    cloneDeck,
    addCardToList,
    addCardsBatchToList,
    getUserWishlistCardIds,
    getUserOfferlistCardIds,
    getUserCardsForCollection,
    BatchAddResult,
    extractCardSetCode,
    cleanSetCodeString,
    extractCardRarity,
    extractCardVariantCode,
    extractCardVariantExternalId
} from '../../services/supabaseService';
import { getGameNameById } from '../../constants/deckRules';
import { exportDeckToText } from '../../utils/deckExporter';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { CardImage } from '../../components/CardImage';
import { CardDetailModal } from '../../components/CardDetailModal';

const getCardCostValue = (card: any): number => {
    if (!card) return 999;
    const val = card.cost ?? card.playCost ?? card.play_cost ?? card.energyCost ?? card.energy_cost ?? card.cmc ?? card.manaCost ?? card.level ?? card.pitch;
    if (val === undefined || val === null || val === '') return 999;
    const parsed = Number(val);
    return isNaN(parsed) ? 999 : parsed;
};

const getSectionStyle = (section: string) => {
    const s = String(section || '').toLowerCase();
    if (s.includes('leader') || s.includes('líder') || s === 'hero' || s === 'legend' || s === 'oshi' || s === 'avatar') {
        return {
            badgeClass: 'bg-amber-500 text-slate-950 font-black',
            borderClass: 'border-amber-500/60 hover:border-amber-400 shadow-amber-500/10',
            label: section || 'Líder',
            icon: 'fa-crown'
        };
    }
    if (s.includes('cheer')) {
        return {
            badgeClass: 'bg-rose-500 text-white font-black',
            borderClass: 'border-rose-500/50 hover:border-rose-400 shadow-rose-500/10',
            label: section || 'Cheer',
            icon: 'fa-heart'
        };
    }
    if (s.includes('monster')) {
        return {
            badgeClass: 'bg-red-600 text-white font-black',
            borderClass: 'border-red-500/50 hover:border-red-400 shadow-red-500/10',
            label: section || 'Monster',
            icon: 'fa-dragon'
        };
    }
    if (s.includes('material')) {
        return {
            badgeClass: 'bg-blue-600 text-white font-black',
            borderClass: 'border-blue-500/50 hover:border-blue-400 shadow-blue-500/10',
            label: section || 'Material',
            icon: 'fa-book-sparkles'
        };
    }
    if (s.includes('don') || s.includes('energy') || s.includes('energia')) {
        return {
            badgeClass: 'bg-pink-600 text-white font-black',
            borderClass: 'border-pink-500/50 hover:border-pink-400 shadow-pink-500/10',
            label: section || 'Don!!',
            icon: 'fa-bolt'
        };
    }
    if (s.includes('side') || s.includes('reserva') || s.includes('extra')) {
        return {
            badgeClass: 'bg-cyan-600 text-white font-black',
            borderClass: 'border-cyan-500/50 hover:border-cyan-400 shadow-cyan-500/10',
            label: section || 'Side',
            icon: 'fa-layer-group'
        };
    }
    return {
        badgeClass: 'bg-slate-800/90 text-slate-300 font-bold',
        borderClass: 'border-slate-800 hover:border-purple-500/60',
        label: section || 'Principal',
        icon: null
    };
};

interface GridCardProps {
    item: any;
    gameId: any;
    hasMultipleSections: boolean;
    showWishlistShortcut: boolean;
    isAddingWishlist: boolean;
    isWishlistAdded: boolean;
    isSelectionMode?: boolean;
    isSelected?: boolean;
    onSelect: (item: any) => void;
    onAddToWishlist: (card: any) => void;
    onToggleSelect?: (card: any) => void;
}

const DeckGridCard = React.memo<GridCardProps>(({
    item,
    gameId,
    hasMultipleSections,
    showWishlistShortcut,
    isAddingWishlist,
    isWishlistAdded,
    isSelectionMode = false,
    isSelected = false,
    onSelect,
    onAddToWishlist,
    onToggleSelect
}) => {
    const cardImageSrc = item.card?.imageUrl || item.card?.image_url || item.card?.image;
    const sectionStyle = getSectionStyle(item.section);

    return (
        <div
            onClick={() => {
                if (isSelectionMode) {
                    onToggleSelect?.(item.card);
                } else {
                    onSelect(item);
                }
            }}
            className={`group relative bg-slate-900 rounded-xl border overflow-hidden shadow-md transition-colors duration-150 cursor-pointer flex flex-col ${
                isSelectionMode && isSelected
                    ? 'border-purple-500 ring-2 ring-purple-500 shadow-purple-500/30'
                    : sectionStyle.borderClass
            }`}
        >
            <div className="relative aspect-[3/4.2] w-full bg-slate-950 overflow-hidden">
                <CardImage
                    src={cardImageSrc}
                    alt={item.card?.name || ''}
                    game={gameId}
                    gameId={gameId}
                    cardBackUrl={item.card?.cardBackUrl || item.card?.card_back_url}
                    className={`w-full h-full object-cover transition-transform duration-200 ${
                        isSelectionMode && isSelected ? 'scale-105 opacity-90' : ''
                    }`}
                />

                {/* Selection Mode Overlay & Indicator */}
                {isSelectionMode ? (
                    <div className="absolute top-1.5 left-1.5 sm:top-2 sm:left-2 z-20">
                        <div className={`w-7 h-7 rounded-xl flex items-center justify-center transition-all shadow-lg ${
                            isSelected
                                ? 'bg-purple-600 text-white ring-2 ring-purple-400 shadow-purple-600/50 scale-105'
                                : 'bg-slate-950/90 text-slate-400 border border-slate-700 hover:border-slate-400 hover:text-white'
                        }`}>
                            <i className={`fas ${isSelected ? 'fa-check' : 'fa-plus'} text-xs font-black`}></i>
                        </div>
                    </div>
                ) : (
                    /* Badge da Seção */
                    <div className="absolute top-1.5 left-1.5 sm:top-2 sm:left-2 z-10 flex items-center space-x-1 max-w-[calc(100%-48px)]">
                        {hasMultipleSections && item.section && (
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-md shadow-md flex items-center space-x-1 shrink-0 ${sectionStyle.badgeClass}`}>
                                {sectionStyle.icon && <i className={`fas ${sectionStyle.icon} text-[8px]`}></i>}
                                <span className="truncate max-w-[65px]">{sectionStyle.label}</span>
                            </span>
                        )}
                    </div>
                )}

                {/* Wishlist Shortcut - Touch visible on mobile, hover on desktop */}
                {!isSelectionMode && showWishlistShortcut && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onAddToWishlist(item.card);
                        }}
                        disabled={isAddingWishlist}
                        className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 z-20 h-7 w-7 sm:h-6 sm:w-6 rounded-lg bg-slate-950/90 hover:bg-pink-600 text-pink-300 hover:text-white border border-pink-500/40 hover:border-pink-500 flex items-center justify-center shadow-md transition-opacity opacity-90 sm:opacity-0 sm:group-hover:opacity-100 cursor-pointer"
                        title="Adicionar à Wishlist"
                    >
                        <i className={`fas ${isAddingWishlist ? 'fa-spinner fa-spin' : (isWishlistAdded ? 'fa-check text-emerald-300' : 'fa-heart')} text-[10px] sm:text-[9px]`}></i>
                    </button>
                )}

                {/* Bottom Shadow Gradient */}
                <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-950 via-slate-950/85 to-transparent pointer-events-none" />

                {/* Bottom Card Information */}
                <div className="absolute bottom-0 inset-x-0 p-1.5 sm:p-2 z-10 flex flex-col justify-end">
                    <p className="text-[11px] sm:text-xs font-bold text-white group-hover:text-purple-300 transition-colors line-clamp-1 leading-tight">
                        {item.card?.name}
                    </p>
                    <div className="flex items-center justify-between mt-0.5 text-[9px] font-mono text-slate-400 font-semibold gap-1">
                        <span className="truncate">{item.card?.code || item.card?.number || ''}</span>
                        <div className="flex items-center space-x-0.5 shrink-0">
                            <span className="text-[10px] text-purple-400 font-bold">x</span>
                            <span className="text-xs text-white font-bold">{item.quantity}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

DeckGridCard.displayName = 'DeckGridCard';

interface ListCardProps {
    item: any;
    gameId?: any;
    hasMultipleSections?: boolean;
    showWishlistShortcut: boolean;
    isAddingWishlist: boolean;
    isWishlistAdded: boolean;
    isSelectionMode?: boolean;
    isSelected?: boolean;
    onSelect: (item: any) => void;
    onAddToWishlist: (card: any) => void;
    onToggleSelect?: (card: any) => void;
}

const DeckListCard = React.memo<ListCardProps>(({
    item,
    hasMultipleSections = false,
    showWishlistShortcut,
    isAddingWishlist,
    isWishlistAdded,
    isSelectionMode = false,
    isSelected = false,
    onSelect,
    onAddToWishlist,
    onToggleSelect
}) => {
    const cardCostVal = getCardCostValue(item.card);
    const sectionStyle = getSectionStyle(item.section);

    return (
        <div 
            onClick={() => {
                if (isSelectionMode) {
                    onToggleSelect?.(item.card);
                } else {
                    onSelect(item);
                }
            }}
            className={`flex items-center justify-between p-2 sm:p-2.5 rounded-xl border transition-all duration-150 shadow-sm cursor-pointer group ${
                isSelectionMode && isSelected
                    ? 'bg-purple-950/60 border-purple-500 ring-1 ring-purple-500/50'
                    : 'bg-slate-900 hover:bg-slate-850 border-slate-800 hover:border-purple-500/40'
            }`}
        >
            <div className="flex items-center space-x-2 sm:space-x-2.5 overflow-hidden min-w-0 flex-1">
                {isSelectionMode ? (
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 transition-all ${
                        isSelected
                            ? 'bg-purple-600 text-white ring-1 ring-purple-400'
                            : 'bg-slate-950 text-slate-500 border border-slate-700'
                    }`}>
                        <i className={`fas ${isSelected ? 'fa-check' : 'fa-plus'} text-[10px]`}></i>
                    </div>
                ) : (
                    <div className="w-7 h-7 rounded-lg bg-purple-950/80 border border-purple-500/30 text-purple-300 font-mono font-black text-xs flex items-center justify-center shrink-0">
                        {item.quantity}x
                    </div>
                )}

                <div className="overflow-hidden min-w-0 flex-1">
                    <div className="flex items-center space-x-1.5 min-w-0">
                        <p className="text-xs font-bold text-slate-200 leading-tight group-hover:text-purple-300 transition-colors truncate min-w-0">
                            {item.card?.name}
                        </p>
                        {cardCostVal !== 999 && (
                            <span className="text-[9px] font-black text-amber-300 bg-amber-500/10 border border-amber-500/20 px-1 py-0.5 rounded shrink-0">
                                {cardCostVal}★
                            </span>
                        )}
                    </div>
                    <div className="flex items-center space-x-1.5 mt-0.5 text-[9px] text-slate-500 font-mono truncate">
                        {hasMultipleSections && item.section && (
                            <span className={`text-[8px] px-1 py-0.2 rounded font-black uppercase tracking-wider shrink-0 ${sectionStyle.badgeClass}`}>
                                {sectionStyle.label}
                            </span>
                        )}
                        <span>{item.card?.code || item.card?.number || item.card?.rarity || ''}</span>
                        {item.card?.rarity && item.card?.code && (
                            <>
                                <span>•</span>
                                <span className="uppercase">{item.card?.rarity}</span>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex items-center space-x-1 shrink-0 ml-1.5 sm:ml-2">
                {!isSelectionMode && showWishlistShortcut && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onAddToWishlist(item.card);
                        }}
                        disabled={isAddingWishlist}
                        className="h-7 w-7 flex items-center justify-center rounded-lg bg-slate-950 hover:bg-pink-600 text-pink-400 hover:text-white border border-pink-500/30 hover:border-pink-500 transition-colors active:scale-95 cursor-pointer shrink-0"
                        title="Adicionar à Wishlist"
                    >
                        <i className={`fas ${isAddingWishlist ? 'fa-spinner fa-spin text-[10px]' : (isWishlistAdded ? 'fa-check text-emerald-300 text-[10px]' : 'fa-heart text-[10px]')}`}></i>
                    </button>
                )}
                {isSelectionMode && (
                    <span className="text-xs font-black text-purple-400 bg-purple-500/10 border border-purple-500/20 h-7 px-2 flex items-center justify-center rounded-lg shrink-0 font-mono">
                        {item.quantity}x
                    </span>
                )}
            </div>
        </div>
    );
});

DeckListCard.displayName = 'DeckListCard';

export const DeckView: React.FC = () => {
    const { deckId } = useParams<{ deckId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { showNotification } = useNotification();
    const [deck, setDeck] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [copiedLink, setCopiedLink] = useState(false);
    const [copiedList, setCopiedList] = useState(false);
    const [isCloning, setIsCloning] = useState(false);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [sortBy, setSortBy] = useState<'cost-asc' | 'cost-desc' | 'name'>('cost-asc');
    const [activeSectionFilter, setActiveSectionFilter] = useState<string>('all');
    const [selectedCard, setSelectedCard] = useState<any | null>(null);
    const [addingWishlistKey, setAddingWishlistKey] = useState<string | null>(null);
    const [wishlistAddedMap, setWishlistAddedMap] = useState<Record<string, boolean>>({});

    useEffect(() => {
        const fetchDeck = async () => {
            if (!deckId) return;
            try {
                const data = await getUserDeck(deckId);
                if (!data) {
                    setError("Deck não encontrado ou privado.");
                } else {
                    setDeck(data);
                }
            } catch (err) {
                console.error(err);
                setError("Erro ao carregar o deck.");
            } finally {
                setLoading(false);
            }
        };
        fetchDeck();
    }, [deckId]);

    const items = useMemo(() => deck?.data?.items || [], [deck]);
    const sections = useMemo(() => Array.from(new Set(items.map((i: any) => i.section))), [items]);
    const totalCards = useMemo(() => items.reduce((acc: number, curr: any) => acc + curr.quantity, 0), [items]);

    // Pre-sort items for each section to avoid repetitive sorting during render (used in list mode)
    const sortedSectionsMap = useMemo(() => {
        const map = new Map<string, { items: any[]; totalQuantity: number }>();
        sections.forEach((section: any) => {
            const raw = items.filter((i: any) => i.section === section);
            const sorted = [...raw].sort((a, b) => {
                const costA = getCardCostValue(a.card);
                const costB = getCardCostValue(b.card);
                if (sortBy === 'cost-asc') {
                    if (costA !== costB) return costA - costB;
                    return (a.card?.name || '').localeCompare(b.card?.name || '');
                }
                if (sortBy === 'cost-desc') {
                    if (costA !== costB) return costB - costA;
                    return (a.card?.name || '').localeCompare(b.card?.name || '');
                }
                if (sortBy === 'name') {
                    return (a.card?.name || '').localeCompare(b.card?.name || '');
                }
                return 0;
            });
            const totalQty = sorted.reduce((acc: number, curr: any) => acc + curr.quantity, 0);
            map.set(section, { items: sorted, totalQuantity: totalQty });
        });
        return map;
    }, [items, sections, sortBy]);

    // Continuous flow of cards without breaking the grid horizontally
    const visibleGridItems = useMemo(() => {
        if (activeSectionFilter !== 'all') {
            return sortedSectionsMap.get(activeSectionFilter)?.items || [];
        }
        const combined: any[] = [];
        sections.forEach((sec: any) => {
            const secData = sortedSectionsMap.get(sec);
            if (secData?.items) {
                combined.push(...secData.items);
            }
        });
        return combined;
    }, [activeSectionFilter, sections, sortedSectionsMap]);

    const leaderItem = useMemo(() => {
        return items.find((i: any) => {
            if (!i || !i.card) return false;
            const section = String(i.section || '').toLowerCase();
            const typeStr = String(i.card.type || i.card.cardType || i.card.category || i.card.supertype || '').toLowerCase();
            return (
                section === 'leader' || 
                section === 'líder' ||
                typeStr.includes('leader') ||
                typeStr.includes('líder')
            );
        }) || items[0];
    }, [items]);

    const leaderImageSrc = leaderItem?.card?.imageUrl || leaderItem?.card?.image_url || leaderItem?.card?.image;

    const handleCopyLink = useCallback(() => {
        navigator.clipboard.writeText(window.location.href);
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
    }, []);

    const handleCopyTextList = useCallback(() => {
        if (!deck) return;
        const gameName = getGameNameById(deck.game_id);
        const text = exportDeckToText(gameName, items);
        navigator.clipboard.writeText(text);
        setCopiedList(true);
        setTimeout(() => setCopiedList(false), 2000);
    }, [deck, items]);

    const handleCloneDeck = useCallback(async () => {
        if (!deck) return;
        if (!user) {
            showNotification('Faça login para clonar este deck para a sua conta.', 'info');
            return;
        }
        setIsCloning(true);
        try {
            const newId = await cloneDeck(deck.id);
            showNotification(`Deck "${deck.name}" clonado com sucesso!`, 'success');
            navigate(`/deck/${newId}`);
        } catch (err) {
            console.error('Error cloning deck:', err);
            showNotification('Erro ao clonar o deck.', 'error');
        } finally {
            setIsCloning(false);
        }
    }, [deck, user, navigate, showNotification]);

    const isPublicDeck = Boolean(deck?.is_public);
    const showWishlistShortcut = isPublicDeck || (deck && deck.user_id !== user?.id);

    const handleAddToWishlist = useCallback(async (cardObj: any, quantity: number = 1) => {
        if (!user) {
            showNotification('Faça login para adicionar cartas à sua Wishlist.', 'info');
            return;
        }

        const cardKey = String(cardObj?.id || cardObj?.code || cardObj?.name);
        setAddingWishlistKey(cardKey);

        try {
            const rawVar = cardObj.variant_number !== undefined && cardObj.variant_number !== null
                ? cardObj.variant_number
                : (cardObj.variantNumber !== undefined && cardObj.variantNumber !== null ? cardObj.variantNumber : 1);
            const variantNumber = Number(rawVar) > 0 ? Number(rawVar) : 1;
            const setCode = extractCardSetCode(cardObj, variantNumber);
            const rarity = extractCardRarity(cardObj, variantNumber);
            const variantCode = extractCardVariantCode(cardObj, variantNumber);
            const variantExternalId = extractCardVariantExternalId(cardObj, variantNumber);

            const cardPayload = {
                ...cardObj,
                variant_number: variantNumber,
                set_code: setCode || (cardObj.set_code ? cleanSetCodeString(cardObj.set_code) : null),
                rarity: rarity || cardObj.rarity || null,
                variant_code: variantCode || cardObj.variant_code || null,
                variant_external_id: variantExternalId || cardObj.variant_external_id || null,
                game_id: cardObj.game_id || deck?.game_id || 1,
                game: cardObj.game || getGameNameById(deck?.game_id),
            };

            await addCardToList(user.id, 'wishlist', cardPayload, quantity);

            setWishlistAddedMap(prev => ({ ...prev, [cardKey]: true }));
            showNotification(`"${cardObj.name}" (${quantity}x) adicionada à sua Wishlist!`, 'success');
            loadUserData();
        } catch (err: any) {
            console.error('Erro ao adicionar à wishlist:', err);
            showNotification(err?.message || 'Não foi possível adicionar à Wishlist.', 'error');
        } finally {
            setAddingWishlistKey(null);
        }
    }, [user, deck, showNotification]);

    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedCardKeys, setSelectedCardKeys] = useState<Set<string>>(new Set());
    const [showOptionsMenu, setShowOptionsMenu] = useState(false);
    const optionsMenuRef = useRef<HTMLDivElement>(null);
    const [showBatchMenu, setShowBatchMenu] = useState(false);
    const batchMenuRef = useRef<HTMLDivElement>(null);

    const [batchModal, setBatchModal] = useState<{
        isOpen: boolean;
        type: 'collection' | 'wishlist' | 'offerlist';
        title: string;
        description: string;
        cardsToProcess: any[];
        onlyMissingOption?: boolean;
        onlyMissingChecked?: boolean;
        useDeckQuantityOption?: boolean;
        useDeckQuantityChecked?: boolean;
    } | null>(null);

    const [batchLoading, setBatchLoading] = useState(false);
    const [batchProgress, setBatchProgress] = useState<{ current: number; total: number; percent: number } | null>(null);
    const [batchResult, setBatchResult] = useState<BatchAddResult | null>(null);

    // User collection, wishlist and offerlist tracking
    const [userWishlistCardIds, setUserWishlistCardIds] = useState<Set<string>>(new Set());
    const [userOfferlistCardIds, setUserOfferlistCardIds] = useState<Set<string>>(new Set());
    const [userOwnedCardIds, setUserOwnedCardIds] = useState<Set<string>>(new Set());

    // Close dropdown menus on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (optionsMenuRef.current && !optionsMenuRef.current.contains(e.target as Node)) {
                setShowOptionsMenu(false);
            }
            if (batchMenuRef.current && !batchMenuRef.current.contains(e.target as Node)) {
                setShowBatchMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const loadUserData = useCallback(async () => {
        if (!user) return;
        try {
            const [wIds, oIds, userCards] = await Promise.all([
                getUserWishlistCardIds(user.id),
                getUserOfferlistCardIds(user.id),
                deck?.game_id ? getUserCardsForCollection(user.id, deck.game_id) : Promise.resolve([])
            ]);
            setUserWishlistCardIds(wIds);
            setUserOfferlistCardIds(oIds);

            const ownedSet = new Set<string>();
            (userCards || []).forEach((uc: any) => {
                if (uc.card_id) ownedSet.add(String(uc.card_id));
                if (uc.id) ownedSet.add(String(uc.id));
                if (uc.external_id) ownedSet.add(String(uc.external_id));
                if (uc.code) ownedSet.add(String(uc.code).toLowerCase());
                if (uc.card?.id) ownedSet.add(String(uc.card.id));
                if (uc.card?.external_id) ownedSet.add(String(uc.card.external_id));
                if (uc.card?.code) ownedSet.add(String(uc.card.code).toLowerCase());
            });
            setUserOwnedCardIds(ownedSet);
        } catch (err) {
            console.error('Error loading user cards info in DeckView:', err);
        }
    }, [user, deck?.game_id]);

    useEffect(() => {
        if (user && deck) {
            loadUserData();
        }
    }, [user, deck, loadUserData]);

    const getCardKey = useCallback((card: any) => {
        return String(card?.id || card?.external_id || card?.code || card?.name || '');
    }, []);

    const isCardInWishlist = useCallback((card: any) => {
        if (!user || !card) return false;
        const cid = String(card.id || '');
        const cext = String(card.external_id || '');
        const ccode = String(card.code || '').toLowerCase();
        return (
            (cid && userWishlistCardIds.has(cid)) ||
            (cext && userWishlistCardIds.has(cext)) ||
            (ccode && userWishlistCardIds.has(ccode)) ||
            Boolean(wishlistAddedMap[getCardKey(card)])
        );
    }, [user, userWishlistCardIds, wishlistAddedMap, getCardKey]);

    const isCardInOfferlist = useCallback((card: any) => {
        if (!user || !card) return false;
        const cid = String(card.id || '');
        const cext = String(card.external_id || '');
        const ccode = String(card.code || '').toLowerCase();
        return (
            (cid && userOfferlistCardIds.has(cid)) ||
            (cext && userOfferlistCardIds.has(cext)) ||
            (ccode && userOfferlistCardIds.has(ccode))
        );
    }, [user, userOfferlistCardIds]);

    const isCardOwned = useCallback((card: any) => {
        if (!user || !card) return false;
        const cid = String(card.id || '');
        const cext = String(card.external_id || '');
        const ccode = String(card.code || '').toLowerCase();
        return (
            (cid && userOwnedCardIds.has(cid)) ||
            (cext && userOwnedCardIds.has(cext)) ||
            (ccode && userOwnedCardIds.has(ccode))
        );
    }, [user, userOwnedCardIds]);

    // Cards formatted with deck attributes (quantity, game, section)
    const deckCardsList = useMemo(() => {
        if (!deck) return [];
        return items.map((i: any) => {
            const rawCard = i.card || {};
            const rawVar = rawCard.variant_number !== undefined && rawCard.variant_number !== null
                ? rawCard.variant_number
                : (rawCard.variantNumber !== undefined && rawCard.variantNumber !== null ? rawCard.variantNumber : 1);
            const variantNumber = Number(rawVar) > 0 ? Number(rawVar) : 1;
            const setCode = extractCardSetCode(rawCard, variantNumber);
            const rarity = extractCardRarity(rawCard, variantNumber);
            const variantCode = extractCardVariantCode(rawCard, variantNumber);
            const variantExternalId = extractCardVariantExternalId(rawCard, variantNumber);

            return {
                ...rawCard,
                variant_number: variantNumber,
                set_code: setCode || (rawCard.set_code ? cleanSetCodeString(rawCard.set_code) : null),
                rarity: rarity || rawCard.rarity || null,
                variant_code: variantCode || rawCard.variant_code || null,
                variant_external_id: variantExternalId || rawCard.variant_external_id || null,
                quantity: i.quantity || 1,
                section: i.section,
                game_id: rawCard.game_id || deck.game_id || 1,
                game: rawCard.game || getGameNameById(deck.game_id)
            };
        });
    }, [items, deck]);

    const currentCardsToOperate = useMemo(() => {
        if (activeSectionFilter !== 'all') {
            return deckCardsList.filter((c: any) => c.section === activeSectionFilter);
        }
        return deckCardsList;
    }, [deckCardsList, activeSectionFilter]);

    const toggleSelectCard = useCallback((card: any, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        const key = getCardKey(card);
        setSelectedCardKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, [getCardKey]);

    const selectAllVisible = useCallback(() => {
        const allKeys = new Set<string>();
        currentCardsToOperate.forEach((c: any) => allKeys.add(getCardKey(c)));
        setSelectedCardKeys(allKeys);
    }, [currentCardsToOperate, getCardKey]);

    const clearSelection = useCallback(() => {
        setSelectedCardKeys(new Set());
    }, []);

    const openBatchModal = useCallback((
        type: 'collection' | 'wishlist' | 'offerlist',
        targetCards: any[],
        title: string,
        description: string,
        options?: {
            onlyMissingOption?: boolean;
            onlyMissingChecked?: boolean;
            useDeckQuantityOption?: boolean;
            useDeckQuantityChecked?: boolean;
        }
    ) => {
        if (!user) {
            showNotification('Faça login para utilizar as ações em lote.', 'info');
            navigate('/login');
            return;
        }
        if (!targetCards || targetCards.length === 0) {
            showNotification('Nenhuma carta encontrada para esta operação.', 'info');
            return;
        }
        setShowBatchMenu(false);
        setShowOptionsMenu(false);
        setBatchProgress(null);
        setBatchResult(null);
        const normalizedTargetCards = targetCards.map((c: any) => {
            const rawVar = c.variant_number !== undefined && c.variant_number !== null
                ? c.variant_number
                : (c.variantNumber !== undefined && c.variantNumber !== null ? c.variantNumber : 1);
            const variantNumber = Number(rawVar) > 0 ? Number(rawVar) : 1;
            const setCode = extractCardSetCode(c, variantNumber);
            const rarity = extractCardRarity(c, variantNumber);
            const variantCode = extractCardVariantCode(c, variantNumber);
            const variantExternalId = extractCardVariantExternalId(c, variantNumber);

            return {
                ...c,
                variant_number: variantNumber,
                set_code: setCode || (c.set_code ? cleanSetCodeString(c.set_code) : null),
                rarity: rarity || c.rarity || null,
                variant_code: variantCode || c.variant_code || null,
                variant_external_id: variantExternalId || c.variant_external_id || null
            };
        });
        setBatchModal({
            isOpen: true,
            type,
            title,
            description,
            cardsToProcess: normalizedTargetCards,
            onlyMissingOption: options?.onlyMissingOption,
            onlyMissingChecked: options?.onlyMissingChecked ?? false,
            useDeckQuantityOption: options?.useDeckQuantityOption ?? true,
            useDeckQuantityChecked: options?.useDeckQuantityChecked ?? true
        });
    }, [user, navigate, showNotification]);

    const executeBatchAction = useCallback(async () => {
        if (!user || !batchModal) return;

        setBatchLoading(true);
        setBatchProgress({ current: 0, total: batchModal.cardsToProcess.length, percent: 0 });
        setBatchResult(null);

        try {
            const targetList = batchModal.type === 'collection' ? 'cards' : (batchModal.type === 'wishlist' ? 'wishlist' : 'offerlist');
            const onlyMissing = Boolean(batchModal.onlyMissingChecked);
            const useCardQuantity = Boolean(batchModal.useDeckQuantityChecked);

            const res = await addCardsBatchToList(
                user.id,
                targetList,
                batchModal.cardsToProcess,
                {
                    onlyMissing,
                    useCardQuantity,
                    onProgress: (current, total) => {
                        const percent = total > 0 ? Math.round((current / total) * 100) : 0;
                        setBatchProgress({ current, total, percent });
                    }
                }
            );

            setBatchResult(res);

            if (targetList === 'cards') {
                await loadUserData();
                showNotification(
                    `${res.added} carta(s) adicionada(s) à sua coleção com sucesso!`,
                    'success'
                );
            } else if (targetList === 'wishlist') {
                await loadUserData();
                showNotification(
                    `${res.added} carta(s) adicionada(s) à sua Lista de Desejos!`,
                    'success'
                );
            } else {
                await loadUserData();
                showNotification(
                    `${res.added} carta(s) adicionada(s) à sua Lista de Trocas (Offerlist)!`,
                    'success'
                );
            }

            if (isSelectionMode) clearSelection();
        } catch (err: any) {
            console.error('Error executing batch action in DeckView:', err);
            showNotification(err?.message || 'Erro ao processar operação em lote.', 'error');
        } finally {
            setBatchLoading(false);
        }
    }, [user, batchModal, isSelectionMode, clearSelection, loadUserData, showNotification]);

    // Modal data memoization
    const expandedCardItem = selectedCard;
    const expandedCard = useMemo(() => {
        if (!expandedCardItem || !deck) return null;
        const base = expandedCardItem.card || expandedCardItem;
        const rawVar = base.variant_number !== undefined && base.variant_number !== null
            ? base.variant_number
            : (base.variantNumber !== undefined && base.variantNumber !== null ? base.variantNumber : 1);
        const variantNumber = Number(rawVar) > 0 ? Number(rawVar) : 1;
        const setCode = extractCardSetCode(base, variantNumber);
        const rarity = extractCardRarity(base, variantNumber);
        const variantCode = extractCardVariantCode(base, variantNumber);
        const variantExternalId = extractCardVariantExternalId(base, variantNumber);

        return {
            ...base,
            variant_number: variantNumber,
            set_code: setCode || base.set_code || null,
            rarity: rarity || base.rarity || null,
            variant_code: variantCode || base.variant_code || null,
            variant_external_id: variantExternalId || base.variant_external_id || null,
            game: base.game || getGameNameById(deck.game_id),
            game_id: base.game_id || deck.game_id,
            section: expandedCardItem.section || base.section,
            quantity: expandedCardItem.quantity || base.quantity || 1,
            imageUrl: base.imageUrl || base.image_url || base.image,
            cardBackUrl: base.cardBackUrl || base.card_back_url
        };
    }, [expandedCardItem, deck]);

    const modalCardsList = useMemo(() => {
        if (!deck) return [];
        return items.map((i: any) => {
            const raw = i.card || i;
            const rawVar = raw.variant_number !== undefined && raw.variant_number !== null
                ? raw.variant_number
                : (raw.variantNumber !== undefined && raw.variantNumber !== null ? raw.variantNumber : 1);
            const variantNumber = Number(rawVar) > 0 ? Number(rawVar) : 1;
            const setCode = extractCardSetCode(raw, variantNumber);
            const rarity = extractCardRarity(raw, variantNumber);
            const variantCode = extractCardVariantCode(raw, variantNumber);
            const variantExternalId = extractCardVariantExternalId(raw, variantNumber);

            return {
                ...raw,
                variant_number: variantNumber,
                set_code: setCode || raw.set_code || null,
                rarity: rarity || raw.rarity || null,
                variant_code: variantCode || raw.variant_code || null,
                variant_external_id: variantExternalId || raw.variant_external_id || null,
                game: raw.game || getGameNameById(deck.game_id),
                game_id: raw.game_id || deck.game_id,
                section: i.section || raw.section,
                quantity: i.quantity || raw.quantity || 1,
                imageUrl: raw.imageUrl || raw.image_url || raw.image,
                cardBackUrl: raw.cardBackUrl || raw.card_back_url
            };
        });
    }, [items, deck]);

    if (loading) {
        return (
            <div className="h-96 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
            </div>
        );
    }

    if (error || !deck) {
        return (
            <div className="h-96 flex flex-col items-center justify-center space-y-4">
                <i className="fas fa-exclamation-triangle text-4xl text-red-500/50"></i>
                <h2 className="text-xl font-black text-white uppercase italic">{error || "Deck não encontrado"}</h2>
                <Link to="/meus-decks" className="text-purple-400 text-xs font-black uppercase tracking-widest hover:text-white transition-colors">Voltar para Meus Decks</Link>
            </div>
        );
    }

    const creatorDisplayName = deck.user?.username 
        ? `@${deck.user.username}` 
        : (deck.user?.fullname || (deck.user_id ? `Criador #${deck.user_id}` : 'Criador Anônimo'));
    const creatorAvatar = deck.user?.avatar;

    return (
        <div className="max-w-7xl mx-auto p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6 md:space-y-8">
            {/* Header do Deck */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-3 sm:p-4 shadow-lg flex flex-col xl:flex-row xl:items-center justify-between gap-3 sm:gap-4">
                {/* Informações do Deck */}
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    {/* Leader - ocupa visualmente as duas linhas */}
                    <div className="relative w-12 h-14 sm:w-14 sm:h-16 md:w-16 md:h-[72px] rounded-xl overflow-hidden bg-slate-950 border border-purple-500/40 shadow-md shrink-0">
                        {leaderImageSrc ? (
                            <>
                                <CardImage
                                    src={leaderImageSrc}
                                    alt={leaderItem?.card?.name || deck.name}
                                    game={deck.game_id}
                                    gameId={deck.game_id}
                                    cardBackUrl={
                                        leaderItem?.card?.cardBackUrl ||
                                        leaderItem?.card?.card_back_url
                                    }
                                    className="w-full h-full object-cover object-top scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent pointer-events-none" />
                            </>
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-purple-400">
                                <i className={`${getGameIcon(deck.game_id)} text-xl`}></i>
                            </div>
                        )}
                    </div>

                    {/* Texto */}
                    <div className="min-w-0 flex-1 flex flex-col justify-center">
                        {/* Linha 1 - Nome + Status */}
                        <div className="flex items-center gap-2 min-w-0 flex-wrap sm:flex-nowrap">
                            <h1 className="text-base sm:text-xl md:text-2xl font-black text-white tracking-tight uppercase italic truncate">
                                {deck.name}
                            </h1>

                            {deck.is_public ? (
                                <span className="shrink-0 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full flex items-center gap-1">
                                    <i className="fas fa-globe text-[8px]"></i>
                                    <span className="hidden sm:inline">Público</span>
                                </span>
                            ) : (
                                <span className="shrink-0 bg-slate-800 text-slate-400 border border-slate-700 text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full">
                                    Privado
                                </span>
                            )}
                        </div>

                        {/* Linha 2 - Informações */}
                        <div className="flex flex-wrap items-center gap-x-2 sm:gap-x-3 gap-y-0.5 mt-1 text-[10px] sm:text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                            <span>
                                Total: <strong className="text-white">{totalCards}</strong> cartas
                            </span>

                            <span className="text-slate-600">·</span>

                            <span>
                                Formato:{" "}
                                <strong className="text-white">
                                    {typeof deck.data?.format === "string"
                                        ? deck.data?.format
                                        : deck.data?.format?.name || "Classic"}
                                </strong>
                            </span>

                            {creatorDisplayName && (
                                <>
                                    <span className="text-slate-600">·</span>
                                    <Link
                                        to={
                                            deck.user?.username
                                                ? `/perfil/${deck.user.username}`
                                                : deck.user?.id
                                                    ? `/perfil/${deck.user.id}`
                                                    : "#"
                                        }
                                        onClick={(e) => {
                                            if (!deck.user?.username && !deck.user?.id) {
                                                e.preventDefault();
                                            }
                                        }}
                                        className="inline-flex items-center gap-1.5 hover:text-purple-300 transition-colors group/creator truncate max-w-[170px] sm:max-w-none"
                                        title={
                                            deck.user?.username
                                                ? `Ver perfil de @${deck.user.username}`
                                                : deck.user?.fullname || "Ver perfil"
                                        }
                                    >
                                        {creatorAvatar ? (
                                            <img
                                                src={creatorAvatar}
                                                alt=""
                                                className="w-4 h-4 rounded-full object-cover shrink-0 group-hover/creator:ring-1 group-hover/creator:ring-purple-400"
                                            />
                                        ) : (
                                            <i className="fas fa-user-circle text-slate-500 text-xs shrink-0 group-hover/creator:text-purple-400"></i>
                                        )}
                                        <span className="truncate">
                                            por{" "}
                                            <strong className="text-purple-300 group-hover/creator:underline">
                                                {creatorDisplayName}
                                            </strong>
                                        </span>
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Controles */}
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 pt-2.5 xl:pt-0 border-t border-slate-800/80 xl:border-t-0 xl:justify-end">
                    {/* Ordenação das Cartas */}
                    <div className="h-8 bg-slate-950 rounded-lg border border-slate-800 flex items-center gap-1 p-1 shrink-0">
                        <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest pl-1 hidden lg:inline-flex items-center gap-1">
                            <i className="fas fa-sort text-purple-400"></i>
                            <span>Ordem:</span>
                        </span>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as any)}
                            className="h-6 bg-slate-900 text-[10px] font-black text-slate-200 border border-slate-800 rounded-md px-1.5 sm:px-2 focus:outline-none focus:border-purple-500 uppercase tracking-wide cursor-pointer max-w-[130px] sm:max-w-none"
                        >
                            <option value="cost-asc">Custo (Menor → Maior)</option>
                            <option value="cost-desc">Custo (Maior → Menor)</option>
                            <option value="name">Nome (A - Z)</option>
                        </select>
                    </div>

                    {/* Botões de Modo de Exibição */}
                    <div className="h-8 bg-slate-950 p-1 rounded-lg border border-slate-800 flex items-center gap-1 shrink-0">
                        <button
                            onClick={() => setViewMode("grid")}
                            className={`h-6 px-2 rounded-md text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 transition-colors ${
                                viewMode === "grid"
                                    ? "bg-purple-600 text-white shadow-sm shadow-purple-600/30"
                                    : "text-slate-400 hover:text-white"
                            }`}
                            title="Modo Grid Visual (Galeria)"
                        >
                            <i className="fas fa-th-large"></i>
                            <span className="hidden sm:inline">Grid</span>
                        </button>

                        <button
                            onClick={() => setViewMode("list")}
                            className={`h-6 px-2 rounded-md text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 transition-colors ${
                                viewMode === "list"
                                    ? "bg-purple-600 text-white shadow-sm shadow-purple-600/30"
                                    : "text-slate-400 hover:text-white"
                            }`}
                            title="Modo Lista Compacta"
                        >
                            <i className="fas fa-list"></i>
                            <span className="hidden sm:inline">Lista</span>
                        </button>
                    </div>

                    {/* Ação Primária: Gerenciar em Meus Decks (Apenas Dono) */}
                    {user && (deck.user_id === user.id || deck.user?.id === user.id) && (
                        <Link
                            to="/meus-decks"
                            className="h-8 px-2.5 sm:px-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-[10px] font-black uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 shadow-sm shadow-purple-600/30 shrink-0"
                            title="Gerenciar este baralho em Meus Decks"
                        >
                            <i className="fas fa-layer-group text-[9px]"></i>
                            <span>
                                <span className="hidden sm:inline">Gerenciar em </span>Meus Decks
                            </span>
                        </Link>
                    )}

                    {/* Dropdown Ações em Lote */}
                    <div className="relative" ref={batchMenuRef}>
                        <button
                            type="button"
                            onClick={() => {
                                setShowBatchMenu((prev) => !prev);
                                setShowOptionsMenu(false);
                            }}
                            className={`h-8 px-2.5 sm:px-3 rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 transition-all cursor-pointer shrink-0 border ${
                                showBatchMenu || isSelectionMode
                                    ? "bg-purple-600/20 text-purple-300 border-purple-500/50"
                                    : "bg-slate-950 hover:bg-slate-900 border-slate-800 text-slate-300 hover:text-white"
                            }`}
                            title="Ações em lote para coleção, wishlist e offerlist"
                        >
                            <i className="fa-solid fa-wand-magic-sparkles text-[10px] text-purple-400"></i>
                            <span className="hidden sm:inline">Ações em </span>
                            <span>Lote</span>
                            {isSelectionMode && (
                                <span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span>
                            )}
                            <i
                                className={`fa-solid fa-chevron-down text-[8px] transition-transform ${
                                    showBatchMenu ? "rotate-180" : ""
                                }`}
                            ></i>
                        </button>

                        {showBatchMenu && (
                            <>
                                <div
                                    className="fixed inset-0 bg-black/65 z-40 sm:hidden"
                                    onClick={() => setShowBatchMenu(false)}
                                />
                                <div className="fixed inset-x-2 bottom-3 sm:bottom-auto sm:inset-x-auto sm:absolute sm:right-0 sm:top-full sm:mt-2 w-auto sm:w-[300px] bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl z-50 overflow-hidden divide-y divide-slate-800 max-h-[75vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-150">
                                    {/* Seção 1: Coleção */}
                                    <div className="p-1.5 space-y-0.5 bg-slate-950">
                                        <span className="text-[9px] font-black uppercase tracking-wider text-purple-400 px-2.5 pt-1 pb-0.5 block">
                                            Coleção
                                        </span>

                                        <button
                                            type="button"
                                            onClick={() => {
                                                openBatchModal(
                                                    "collection",
                                                    currentCardsToOperate,
                                                    "Adicionar Todas à Coleção",
                                                    `Adicionar todas as ${currentCardsToOperate.length} cartas do deck à sua coleção pessoal.`,
                                                    {
                                                        onlyMissingOption: true,
                                                        onlyMissingChecked: false,
                                                        useDeckQuantityOption: true,
                                                        useDeckQuantityChecked: true,
                                                    }
                                                );
                                            }}
                                            className="w-full text-left px-2.5 py-1.5 rounded-xl text-xs text-slate-200 hover:text-white hover:bg-slate-900 flex items-center gap-2.5 transition-colors cursor-pointer"
                                        >
                                            <i className="fa-solid fa-layer-group text-purple-400 w-4 text-center text-xs"></i>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-xs truncate">
                                                    Adicionar todas à Coleção
                                                </div>
                                                <div className="text-[10px] text-slate-400 truncate">
                                                    {currentCardsToOperate.length} cartas ({totalCards} cópias no deck)
                                                </div>
                                            </div>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => {
                                                const missingCards = currentCardsToOperate.filter(
                                                    (c: any) => !isCardOwned(c)
                                                );
                                                openBatchModal(
                                                    "collection",
                                                    missingCards,
                                                    "Adicionar Apenas Faltantes à Coleção",
                                                    `Adicionar à sua coleção as ${missingCards.length} cartas deste deck que você ainda não possui.`,
                                                    {
                                                        onlyMissingOption: false,
                                                        onlyMissingChecked: true,
                                                        useDeckQuantityOption: true,
                                                        useDeckQuantityChecked: true,
                                                    }
                                                );
                                            }}
                                            className="w-full text-left px-2.5 py-1.5 rounded-xl text-xs text-slate-200 hover:text-white hover:bg-slate-900 flex items-center gap-2.5 transition-colors cursor-pointer"
                                        >
                                            <i className="fa-solid fa-circle-plus text-emerald-400 w-4 text-center text-xs"></i>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-xs truncate">
                                                    Adicionar apenas faltantes
                                                </div>
                                                <div className="text-[10px] text-slate-400 truncate">
                                                    {currentCardsToOperate.filter((c: any) => !isCardOwned(c)).length} cartas restantes
                                                </div>
                                            </div>
                                        </button>
                                    </div>

                                    {/* Seção 2: Wishlist & Trocas */}
                                    <div className="p-1.5 space-y-0.5 bg-slate-950">
                                        <span className="text-[9px] font-black uppercase tracking-wider text-rose-400 px-2.5 pt-1 pb-0.5 block">
                                            Lista de Desejos & Trocas
                                        </span>

                                        <button
                                            type="button"
                                            onClick={() => {
                                                openBatchModal(
                                                    "wishlist",
                                                    currentCardsToOperate,
                                                    "Adicionar Todas à Wishlist",
                                                    `Adicionar todas as ${currentCardsToOperate.length} cartas do deck à sua lista de desejos.`,
                                                    {
                                                        onlyMissingOption: true,
                                                        onlyMissingChecked: false,
                                                        useDeckQuantityOption: true,
                                                        useDeckQuantityChecked: true,
                                                    }
                                                );
                                            }}
                                            className="w-full text-left px-2.5 py-1.5 rounded-xl text-xs text-slate-200 hover:text-white hover:bg-slate-900 flex items-center gap-2.5 transition-colors cursor-pointer"
                                        >
                                            <i className="fa-solid fa-heart text-rose-400 w-4 text-center text-xs"></i>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-xs truncate">
                                                    Adicionar todas à Wishlist
                                                </div>
                                                <div className="text-[10px] text-slate-400 truncate">
                                                    {currentCardsToOperate.length} cartas do deck
                                                </div>
                                            </div>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => {
                                                const missingWishlist = currentCardsToOperate.filter(
                                                    (c: any) => !isCardOwned(c) && !isCardInWishlist(c)
                                                );
                                                openBatchModal(
                                                    "wishlist",
                                                    missingWishlist,
                                                    "Adicionar Faltantes à Wishlist",
                                                    `Adicionar à Wishlist as ${missingWishlist.length} cartas do deck que você ainda não possui na coleção e não favoritou.`,
                                                    {
                                                        onlyMissingOption: false,
                                                        useDeckQuantityOption: true,
                                                        useDeckQuantityChecked: true,
                                                    }
                                                );
                                            }}
                                            className="w-full text-left px-2.5 py-1.5 rounded-xl text-xs text-slate-200 hover:text-white hover:bg-slate-900 flex items-center gap-2.5 transition-colors cursor-pointer"
                                        >
                                            <i className="fa-regular fa-heart text-rose-400 w-4 text-center text-xs"></i>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-xs truncate">
                                                    Adicionar faltantes à Wishlist
                                                </div>
                                                <div className="text-[10px] text-slate-400 truncate">
                                                    Fora da coleção e da wishlist
                                                </div>
                                            </div>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => {
                                                openBatchModal(
                                                    "offerlist",
                                                    currentCardsToOperate,
                                                    "Adicionar Todas à Offerlist",
                                                    `Adicionar todas as ${currentCardsToOperate.length} cartas do deck à sua lista de trocas.`,
                                                    {
                                                        onlyMissingOption: true,
                                                        onlyMissingChecked: false,
                                                        useDeckQuantityOption: true,
                                                        useDeckQuantityChecked: false,
                                                    }
                                                );
                                            }}
                                            className="w-full text-left px-2.5 py-1.5 rounded-xl text-xs text-slate-200 hover:text-white hover:bg-slate-900 flex items-center gap-2.5 transition-colors cursor-pointer"
                                        >
                                            <i className="fa-solid fa-arrow-right-arrow-left text-amber-400 w-4 text-center text-xs"></i>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-xs truncate">
                                                    Adicionar à Lista de Trocas
                                                </div>
                                                <div className="text-[10px] text-slate-400 truncate">
                                                    Disponibilizar cartas para negociações
                                                </div>
                                            </div>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => {
                                                const ownedCards = currentCardsToOperate.filter(
                                                    (c: any) => isCardOwned(c)
                                                );
                                                openBatchModal(
                                                    "offerlist",
                                                    ownedCards,
                                                    "Adicionar Cartas Possuídas à Offerlist",
                                                    `Adicionar à lista de trocas as ${ownedCards.length} cartas deste deck que você já possui na sua coleção.`,
                                                    {
                                                        onlyMissingOption: false,
                                                        onlyMissingChecked: true,
                                                        useDeckQuantityOption: true,
                                                        useDeckQuantityChecked: false,
                                                    }
                                                );
                                            }}
                                            className="w-full text-left px-2.5 py-1.5 rounded-xl text-xs text-slate-200 hover:text-white hover:bg-slate-900 flex items-center gap-2.5 transition-colors cursor-pointer"
                                        >
                                            <i className="fa-solid fa-handshake text-amber-400 w-4 text-center text-xs"></i>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-xs truncate">
                                                    Adicionar cartas possuídas
                                                </div>
                                                <div className="text-[10px] text-slate-400 truncate">
                                                    {currentCardsToOperate.filter((c: any) => isCardOwned(c)).length} cartas que você possui
                                                </div>
                                            </div>
                                        </button>
                                    </div>

                                    {/* Seção 3: Ferramentas */}
                                    <div className="p-1.5 bg-slate-950">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsSelectionMode((prev) => {
                                                    const next = !prev;
                                                    if (!next) {
                                                        clearSelection();
                                                    }
                                                    return next;
                                                });
                                                setShowBatchMenu(false);
                                            }}
                                            className={`w-full text-left px-2.5 py-2 rounded-xl text-xs font-semibold flex items-center gap-2.5 transition-colors cursor-pointer ${
                                                isSelectionMode
                                                    ? "bg-purple-950 text-purple-300 border border-purple-500/50"
                                                    : "text-slate-200 hover:text-white hover:bg-slate-900"
                                            }`}
                                        >
                                            <i
                                                className={`fa-solid ${
                                                    isSelectionMode
                                                        ? "fa-square-check text-purple-400"
                                                        : "fa-list-check text-slate-400"
                                                } w-4 text-center text-xs`}
                                            ></i>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-xs truncate">
                                                    {isSelectionMode
                                                        ? "Desativar Modo de Seleção"
                                                        : "Ativar Modo de Seleção"}
                                                </div>
                                                <div className="text-[10px] text-slate-400 truncate">
                                                    {isSelectionMode
                                                        ? "Clique para desmarcar tudo e sair"
                                                        : "Marque cartas individualmente para lote"}
                                                </div>
                                            </div>
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Dropdown Opções */}
                    <div className="relative" ref={optionsMenuRef}>
                        <button
                            type="button"
                            onClick={() => {
                                setShowOptionsMenu((prev) => !prev);
                                setShowBatchMenu(false);
                            }}
                            className={`h-8 px-2.5 sm:px-3 rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 transition-all cursor-pointer shrink-0 border ${
                                showOptionsMenu
                                    ? "bg-purple-950/70 border-purple-500/60 text-purple-300"
                                    : "bg-slate-950 hover:bg-slate-900 border-slate-800 text-slate-300 hover:text-white"
                            }`}
                            title="Mais opções do deck"
                        >
                            <i className="fa-solid fa-ellipsis-vertical text-[11px] text-purple-400"></i>
                            <span>Opções</span>
                            <i
                                className={`fa-solid fa-chevron-down text-[8px] transition-transform ${
                                    showOptionsMenu ? "rotate-180" : ""
                                }`}
                            ></i>
                        </button>

                        {showOptionsMenu && (
                            <>
                                <div
                                    className="fixed inset-0 bg-black/65 z-40 sm:hidden"
                                    onClick={() => setShowOptionsMenu(false)}
                                />
                                <div className="fixed inset-x-2 bottom-3 sm:bottom-auto sm:inset-x-auto sm:absolute sm:right-0 sm:top-full sm:mt-2 w-auto sm:w-[280px] bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl z-50 overflow-hidden divide-y divide-slate-800 max-h-[75vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-150">
                                    <div className="p-1.5 space-y-0.5 bg-slate-950">
                                        {/* Clonar Deck */}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowOptionsMenu(false);
                                                handleCloneDeck();
                                            }}
                                            disabled={isCloning}
                                            className="w-full text-left px-2.5 py-2 rounded-xl text-xs text-slate-200 hover:text-white hover:bg-slate-900 flex items-center gap-2.5 transition-colors cursor-pointer disabled:opacity-50"
                                        >
                                            <i className={`fas ${isCloning ? "fa-spinner fa-spin" : "fa-copy"} text-purple-400 w-4 text-center text-xs`}></i>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-xs truncate">
                                                    {user && (deck.user_id === user.id || deck.user?.id === user.id)
                                                        ? "Duplicar este Deck"
                                                        : "Clonar Deck"}
                                                </div>
                                                <div className="text-[10px] text-slate-400 truncate">
                                                    {user && (deck.user_id === user.id || deck.user?.id === user.id)
                                                        ? "Cria uma cópia idêntica em Meus Decks"
                                                        : "Salvar uma cópia no seu perfil"}
                                                </div>
                                            </div>
                                        </button>

                                        {/* Copiar Link */}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                handleCopyLink();
                                                setTimeout(() => setShowOptionsMenu(false), 600);
                                            }}
                                            className="w-full text-left px-2.5 py-2 rounded-xl text-xs text-slate-200 hover:text-white hover:bg-slate-900 flex items-center gap-2.5 transition-colors cursor-pointer"
                                        >
                                            <i className={`fas ${copiedLink ? "fa-check text-emerald-400" : "fa-link text-purple-400"} w-4 text-center text-xs`}></i>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-xs truncate">
                                                    {copiedLink ? "Link copiado!" : "Copiar Link do Deck"}
                                                </div>
                                                <div className="text-[10px] text-slate-400 truncate">
                                                    Compartilhe a página com outros jogadores
                                                </div>
                                            </div>
                                        </button>

                                        {/* Copiar Lista de Texto */}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                handleCopyTextList();
                                                setTimeout(() => setShowOptionsMenu(false), 600);
                                            }}
                                            className="w-full text-left px-2.5 py-2 rounded-xl text-xs text-slate-200 hover:text-white hover:bg-slate-900 flex items-center gap-2.5 transition-colors cursor-pointer"
                                        >
                                            <i className={`fas ${copiedList ? "fa-check text-emerald-400" : "fa-file-lines text-blue-400"} w-4 text-center text-xs`}></i>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-xs truncate">
                                                    {copiedList ? "Lista copiada!" : "Copiar Lista em Texto"}
                                                </div>
                                                <div className="text-[10px] text-slate-400 truncate">
                                                    Exportar texto para simuladores ou anotações
                                                </div>
                                            </div>
                                        </button>

                                        {/* Atalho para Gerenciar em Meus Decks se dono */}
                                        {user && (deck.user_id === user.id || deck.user?.id === user.id) && (
                                            <Link
                                                to="/meus-decks"
                                                onClick={() => setShowOptionsMenu(false)}
                                                className="w-full text-left px-2.5 py-2 rounded-xl text-xs text-slate-200 hover:text-white hover:bg-slate-900 flex items-center gap-2.5 transition-colors cursor-pointer"
                                            >
                                                <i className="fas fa-layer-group text-pink-400 w-4 text-center text-xs"></i>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-semibold text-xs truncate">
                                                        Gerenciar em Meus Decks
                                                    </div>
                                                    <div className="text-[10px] text-slate-400 truncate">
                                                        Editar cartas, descrição ou excluir deck
                                                    </div>
                                                </div>
                                            </Link>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Conteúdo das Cartas */}
            {viewMode === 'grid' ? (
                /* GRID VISUAL ÚNICO SEM QUEBRAS HORIZONTAIS */
                <div className="space-y-4">
                    {/* Filtros de Seção quando houver mais de uma */}
                    {sections.length > 1 && (
                        <div className="flex items-center gap-1.5 sm:gap-2 pb-1 overflow-x-auto no-scrollbar scroll-smooth -mx-1 px-1 sm:mx-0 sm:px-0 sm:flex-wrap">
                            <button
                                type="button"
                                onClick={() => setActiveSectionFilter('all')}
                                className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-colors cursor-pointer flex items-center space-x-1.5 shrink-0 ${
                                    activeSectionFilter === 'all'
                                        ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                                        : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
                                }`}
                            >
                                <span>Todas</span>
                                <span className="bg-slate-950/80 px-1.5 py-0.5 rounded-md text-[10px] text-purple-300 font-mono">
                                    {totalCards}
                                </span>
                            </button>
                            {sections.map((section: any) => {
                                const sectionData = sortedSectionsMap.get(section);
                                const qty = sectionData?.totalQuantity || 0;
                                const style = getSectionStyle(section);

                                return (
                                    <button
                                        key={section}
                                        type="button"
                                        onClick={() => setActiveSectionFilter(section)}
                                        className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-colors cursor-pointer flex items-center space-x-1.5 shrink-0 ${
                                            activeSectionFilter === section
                                                ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                                                : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
                                        }`}
                                    >
                                        {style.icon && <i className={`fas ${style.icon} text-[10px]`}></i>}
                                        <span>{section}</span>
                                        <span className="bg-slate-950/80 px-1.5 py-0.5 rounded-md text-[10px] text-purple-300 font-mono">
                                            {qty}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5 sm:gap-3.5 md:gap-4">
                        {visibleGridItems.map((item: any, idx: number) => {
                            const cardKey = String(item.card?.id || item.card?.code || item.card?.name);
                            return (
                                <DeckGridCard
                                    key={`${item.section}-${cardKey}-${idx}`}
                                    item={item}
                                    gameId={deck.game_id}
                                    hasMultipleSections={sections.length > 1}
                                    showWishlistShortcut={showWishlistShortcut}
                                    isAddingWishlist={addingWishlistKey === cardKey}
                                    isWishlistAdded={Boolean(wishlistAddedMap[cardKey])}
                                    isSelectionMode={isSelectionMode}
                                    isSelected={selectedCardKeys.has(cardKey)}
                                    onSelect={setSelectedCard}
                                    onAddToWishlist={(card) => handleAddToWishlist(card, 1)}
                                    onToggleSelect={toggleSelectCard}
                                />
                            );
                        })}
                    </div>
                </div>
            ) : (
                /* MODO LISTA EM GRID (SEM IMAGENS) */
                <div className="space-y-4">
                    {/* Filtros de Seção quando houver mais de uma */}
                    {sections.length > 1 && (
                        <div className="flex items-center gap-1.5 sm:gap-2 pb-1 overflow-x-auto no-scrollbar scroll-smooth -mx-1 px-1 sm:mx-0 sm:px-0 sm:flex-wrap">
                            <button
                                type="button"
                                onClick={() => setActiveSectionFilter('all')}
                                className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-colors cursor-pointer flex items-center space-x-1.5 shrink-0 ${
                                    activeSectionFilter === 'all'
                                        ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                                        : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
                                }`}
                            >
                                <span>Todas</span>
                                <span className="bg-slate-950/80 px-1.5 py-0.5 rounded-md text-[10px] text-purple-300 font-mono">
                                    {totalCards}
                                </span>
                            </button>
                            {sections.map((section: any) => {
                                const sectionData = sortedSectionsMap.get(section);
                                const qty = sectionData?.totalQuantity || 0;
                                const style = getSectionStyle(section);

                                return (
                                    <button
                                        key={section}
                                        type="button"
                                        onClick={() => setActiveSectionFilter(section)}
                                        className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-colors cursor-pointer flex items-center space-x-1.5 shrink-0 ${
                                            activeSectionFilter === section
                                                ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                                                : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
                                        }`}
                                    >
                                        {style.icon && <i className={`fas ${style.icon} text-[10px]`}></i>}
                                        <span>{section}</span>
                                        <span className="bg-slate-950/80 px-1.5 py-0.5 rounded-md text-[10px] text-purple-300 font-mono">
                                            {qty}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Grid de Cartas do Modo Lista (Sem Imagens) */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 sm:gap-2.5">
                        {visibleGridItems.map((item: any, idx: number) => {
                            const cardKey = String(item.card?.id || item.card?.code || item.card?.name);
                            return (
                                <DeckListCard
                                    key={`${item.section}-${cardKey}-${idx}`}
                                    item={item}
                                    gameId={deck.game_id}
                                    hasMultipleSections={sections.length > 1}
                                    showWishlistShortcut={showWishlistShortcut}
                                    isAddingWishlist={addingWishlistKey === cardKey}
                                    isWishlistAdded={Boolean(wishlistAddedMap[cardKey])}
                                    isSelectionMode={isSelectionMode}
                                    isSelected={selectedCardKeys.has(cardKey)}
                                    onSelect={setSelectedCard}
                                    onAddToWishlist={(card) => handleAddToWishlist(card, 1)}
                                    onToggleSelect={toggleSelectCard}
                                />
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Floating Selection Bar */}
            <AnimatePresence>
                {isSelectionMode && (
                    <motion.div
                        initial={{ y: 50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 50, opacity: 0 }}
                        className="fixed bottom-3 sm:bottom-6 inset-x-2 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 z-40 bg-slate-900/98 border border-purple-500/40 shadow-2xl shadow-black/80 rounded-2xl p-2.5 sm:px-4 sm:py-2.5 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 text-xs text-white sm:max-w-2xl"
                    >
                        {/* Top row on mobile / Left zone on desktop: count & select all & close */}
                        <div className="flex items-center justify-between gap-2 sm:gap-3 sm:border-r sm:border-slate-800 sm:pr-3 shrink-0">
                            <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-lg bg-purple-600 text-white font-black flex items-center justify-center text-xs shrink-0">
                                    {selectedCardKeys.size}
                                </span>
                                <span className="font-bold text-slate-300 text-[11px] sm:text-xs">
                                    {selectedCardKeys.size === 1 ? 'marcada' : 'marcadas'}
                                </span>
                            </div>

                            <div className="flex items-center gap-1.5">
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (selectedCardKeys.size === currentCardsToOperate.length) {
                                            clearSelection();
                                        } else {
                                            selectAllVisible();
                                        }
                                    }}
                                    className="px-2 py-1 sm:px-2.5 sm:py-1.5 rounded-lg sm:rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white font-semibold transition-colors cursor-pointer text-[10px] sm:text-xs"
                                >
                                    {selectedCardKeys.size === currentCardsToOperate.length ? 'Desmarcar' : `Todas (${currentCardsToOperate.length})`}
                                </button>

                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsSelectionMode(false);
                                        clearSelection();
                                    }}
                                    className="sm:hidden px-2 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors cursor-pointer"
                                    title="Sair do modo seleção"
                                >
                                    <i className="fa-solid fa-xmark text-xs"></i>
                                </button>
                            </div>
                        </div>

                        {/* Actions row: 3 action buttons + close on desktop */}
                        <div className="grid grid-cols-3 sm:flex sm:items-center gap-1.5 sm:gap-2 flex-1">
                            <button
                                type="button"
                                disabled={selectedCardKeys.size === 0}
                                onClick={() => {
                                    const selCards = deckCardsList.filter((c: any) => selectedCardKeys.has(getCardKey(c)));
                                    openBatchModal(
                                        'collection',
                                        selCards,
                                        'Adicionar Selecionadas à Coleção',
                                        `Adicionar ${selCards.length} carta(s) selecionada(s) à sua coleção.`,
                                        { onlyMissingOption: true, onlyMissingChecked: false, useDeckQuantityOption: true, useDeckQuantityChecked: true }
                                    );
                                }}
                                className="px-2 sm:px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold flex items-center justify-center gap-1 transition-all shadow-sm cursor-pointer text-[10px] sm:text-xs"
                            >
                                <i className="fa-solid fa-plus text-[9px]"></i>
                                <span className="truncate">Coleção</span>
                            </button>

                            <button
                                type="button"
                                disabled={selectedCardKeys.size === 0}
                                onClick={() => {
                                    const selCards = deckCardsList.filter((c: any) => selectedCardKeys.has(getCardKey(c)));
                                    openBatchModal(
                                        'wishlist',
                                        selCards,
                                        'Adicionar Selecionadas à Wishlist',
                                        `Adicionar ${selCards.length} carta(s) selecionada(s) à sua Lista de Desejos.`,
                                        { onlyMissingOption: false, useDeckQuantityOption: true, useDeckQuantityChecked: true }
                                    );
                                }}
                                className="px-2 sm:px-3 py-1.5 rounded-xl bg-rose-600 hover:bg-rose-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold flex items-center justify-center gap-1 transition-all shadow-sm cursor-pointer text-[10px] sm:text-xs"
                            >
                                <i className="fa-solid fa-heart text-[9px]"></i>
                                <span className="truncate">Wishlist</span>
                            </button>

                            <button
                                type="button"
                                disabled={selectedCardKeys.size === 0}
                                onClick={() => {
                                    const selCards = deckCardsList.filter((c: any) => selectedCardKeys.has(getCardKey(c)));
                                    openBatchModal(
                                        'offerlist',
                                        selCards,
                                        'Adicionar Selecionadas à Offerlist',
                                        `Adicionar ${selCards.length} carta(s) selecionada(s) à sua Lista de Ofertas (Trocas).`,
                                        { onlyMissingOption: false, useDeckQuantityOption: true, useDeckQuantityChecked: false }
                                    );
                                }}
                                className="px-2 sm:px-3 py-1.5 rounded-xl bg-amber-600 hover:bg-amber-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold flex items-center justify-center gap-1 transition-all shadow-sm cursor-pointer text-[10px] sm:text-xs"
                            >
                                <i className="fa-solid fa-arrow-right-arrow-left text-[9px]"></i>
                                <span className="truncate">Trocas</span>
                            </button>

                            <button
                                type="button"
                                onClick={() => {
                                    setIsSelectionMode(false);
                                    clearSelection();
                                }}
                                className="hidden sm:flex px-2.5 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors cursor-pointer shrink-0"
                                title="Sair do modo seleção"
                            >
                                <i className="fa-solid fa-xmark text-xs"></i>
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Batch Operation Modal */}
            <AnimatePresence>
                {batchModal && batchModal.isOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/85 animate-in fade-in duration-200">
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-slate-900 border border-slate-700 rounded-3xl p-4 sm:p-6 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl space-y-4 sm:space-y-5 text-slate-100 relative"
                        >
                            {/* Close button */}
                            <button
                                type="button"
                                onClick={() => setBatchModal(null)}
                                className="absolute top-4 right-4 text-slate-400 hover:text-white w-8 h-8 rounded-full bg-slate-800/80 hover:bg-slate-700 flex items-center justify-center transition-colors cursor-pointer z-10"
                                title="Fechar"
                            >
                                <i className="fa-solid fa-xmark text-sm"></i>
                            </button>

                            {/* Header */}
                            <div className="flex items-start gap-3 sm:gap-4 pr-8">
                                <div
                                    className={`w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center text-lg sm:text-xl shrink-0 ${
                                        batchModal.type === 'collection'
                                            ? 'bg-purple-950/80 text-purple-400 border border-purple-500/40'
                                            : batchModal.type === 'wishlist'
                                            ? 'bg-rose-950/80 text-rose-400 border border-rose-500/40'
                                            : 'bg-amber-950/80 text-amber-400 border border-amber-500/40'
                                    }`}
                                >
                                    <i
                                        className={`fa-solid ${
                                            batchModal.type === 'collection'
                                                ? 'fa-layer-group'
                                                : batchModal.type === 'wishlist'
                                                ? 'fa-heart'
                                                : 'fa-arrow-right-arrow-left'
                                        }`}
                                    ></i>
                                </div>

                                <div className="space-y-1 flex-1 min-w-0">
                                    <h3 className="text-base sm:text-lg font-bold text-white leading-snug">
                                        {batchModal.title}
                                    </h3>
                                    <p className="text-xs text-slate-400 leading-relaxed">
                                        {batchModal.description}
                                    </p>
                                </div>
                            </div>

                            {/* Progress State */}
                            {batchLoading && (
                                <div className="space-y-3 py-3 border-y border-slate-800">
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-slate-300 font-semibold flex items-center gap-2">
                                            <i className="fa-solid fa-spinner fa-spin text-purple-400"></i>
                                            <span>Sincronizando e adicionando...</span>
                                        </span>
                                        <span className="font-mono text-purple-400 font-bold">
                                            {batchProgress?.percent || 0}%
                                        </span>
                                    </div>

                                    <div className="w-full bg-slate-950 rounded-full h-3 overflow-hidden border border-slate-800">
                                        <div
                                            className="bg-gradient-to-r from-purple-500 to-emerald-400 h-full transition-all duration-300 rounded-full"
                                            style={{ width: `${batchProgress?.percent || 0}%` }}
                                        />
                                    </div>

                                    <div className="text-[11px] text-slate-400 text-center">
                                        Processadas {batchProgress?.current || 0} de {batchProgress?.total || 0} cartas
                                    </div>
                                </div>
                            )}

                            {/* Result State */}
                            {!batchLoading && batchResult && (
                                <div className="space-y-3 py-2">
                                    <div className="bg-emerald-950/30 border border-emerald-500/30 rounded-2xl p-4 text-xs space-y-2">
                                        <div className="flex items-center gap-2 text-emerald-400 font-bold">
                                            <i className="fa-solid fa-circle-check text-sm"></i>
                                            <span>Operação concluída com sucesso!</span>
                                        </div>
                                        <div className="grid grid-cols-3 gap-2 text-center pt-2">
                                            <div className="bg-slate-950/60 p-2 rounded-xl">
                                                <div className="text-[10px] text-slate-400 uppercase">Adicionadas</div>
                                                <div className="text-base font-black text-emerald-400">{batchResult.added}</div>
                                            </div>
                                            <div className="bg-slate-950/60 p-2 rounded-xl">
                                                <div className="text-[10px] text-slate-400 uppercase">Atualizadas</div>
                                                <div className="text-base font-black text-purple-400">{batchResult.updated}</div>
                                            </div>
                                            <div className="bg-slate-950/60 p-2 rounded-xl">
                                                <div className="text-[10px] text-slate-400 uppercase">Ignoradas</div>
                                                <div className="text-base font-black text-slate-400">{batchResult.skipped}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Config Options when Idle */}
                            {!batchLoading && !batchResult && (
                                <div className="space-y-3">
                                    <div className="bg-slate-950/70 border border-slate-800 rounded-2xl p-3 sm:p-3.5 space-y-2">
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="text-slate-400">Total de cartas a processar:</span>
                                            <strong className="text-white font-mono text-sm">{batchModal.cardsToProcess.length}</strong>
                                        </div>

                                        {batchModal.useDeckQuantityOption && (
                                            <label className="flex items-center gap-2 pt-2 border-t border-slate-800/80 text-xs text-slate-300 cursor-pointer select-none">
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(batchModal.useDeckQuantityChecked)}
                                                    onChange={e =>
                                                        setBatchModal(prev =>
                                                            prev ? { ...prev, useDeckQuantityChecked: e.target.checked } : null
                                                        )
                                                    }
                                                    className="rounded border-slate-700 bg-slate-900 text-purple-600 focus:ring-purple-500 w-4 h-4 cursor-pointer"
                                                />
                                                <span>
                                                    Usar quantidade exata do deck (ex: se o deck usa 4x, adiciona 4 cópias)
                                                </span>
                                            </label>
                                        )}

                                        {(batchModal.type === 'collection' || batchModal.type === 'offerlist') && batchModal.onlyMissingOption && (
                                            <label className="flex items-center gap-2 pt-2 border-t border-slate-800/80 text-xs text-slate-300 cursor-pointer select-none">
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(batchModal.onlyMissingChecked)}
                                                    onChange={e =>
                                                        setBatchModal(prev =>
                                                            prev ? { ...prev, onlyMissingChecked: e.target.checked } : null
                                                        )
                                                    }
                                                    className="rounded border-slate-700 bg-slate-900 text-purple-600 focus:ring-purple-500 w-4 h-4 cursor-pointer"
                                                />
                                                <span>
                                                    {batchModal.type === 'collection'
                                                        ? 'Apenas adicionar cartas que ainda não possuo na coleção'
                                                        : 'Apenas adicionar cartas que ainda não estão na minha lista de trocas'}
                                                </span>
                                            </label>
                                        )}
                                    </div>

                                    <p className="text-[11px] text-slate-400 leading-normal">
                                        {batchModal.type === 'collection'
                                            ? 'As cartas serão sincronizadas com o catálogo oficial e adicionadas à sua coleção.'
                                            : batchModal.type === 'wishlist'
                                            ? 'As cartas serão salvas na sua lista de desejos para que você acompanhe suas buscas e negociações.'
                                            : 'As cartas serão adicionadas à sua lista de ofertas (trocas) para ficarem disponíveis em matches e negociações do CardLink.'}
                                    </p>
                                </div>
                            )}

                            {/* Actions Footer */}
                            <div className="flex items-center justify-end gap-2.5 pt-2">
                                {!batchLoading && !batchResult && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => setBatchModal(null)}
                                            className="px-3.5 sm:px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold transition-colors cursor-pointer"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="button"
                                            onClick={executeBatchAction}
                                            className={`px-4 sm:px-5 py-2 rounded-xl text-white text-xs font-bold transition-all shadow-lg cursor-pointer flex items-center gap-1.5 ${
                                                batchModal.type === 'wishlist'
                                                    ? 'bg-rose-600 hover:bg-rose-500 shadow-rose-600/30'
                                                    : batchModal.type === 'offerlist'
                                                    ? 'bg-amber-600 hover:bg-amber-500 shadow-amber-600/30'
                                                    : 'bg-purple-600 hover:bg-purple-500 shadow-purple-600/30'
                                            }`}
                                        >
                                            <i className="fa-solid fa-bolt text-[11px]"></i>
                                            <span>Confirmar e Adicionar ({batchModal.cardsToProcess.length})</span>
                                        </button>
                                    </>
                                )}

                                {batchResult && (
                                    <button
                                        type="button"
                                        onClick={() => setBatchModal(null)}
                                        className="w-full py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-all shadow-md shadow-purple-600/30 cursor-pointer"
                                    >
                                        Fechar
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Modal Completo de Detalhes da Carta */}
            <CardDetailModal
                card={expandedCard}
                onClose={() => setSelectedCard(null)}
                cardsList={modalCardsList}
                onSelectCard={(cardObj) => {
                    const foundItem = items.find((i: any) => (i.card?.id || i.id) === (cardObj.id || cardObj.code) || (i.card?.code && i.card?.code === cardObj.code));
                    setSelectedCard(foundItem || cardObj);
                }}
                gameName={getGameNameById(deck?.game_id)}
                deckInfo={expandedCard ? {
                    section: expandedCard.section,
                    quantity: expandedCard.quantity
                } : undefined}
                wishlistAction={expandedCard ? {
                    isAdded: Boolean(wishlistAddedMap[String(expandedCard.id || expandedCard.code || expandedCard.name)]),
                    loading: addingWishlistKey === String(expandedCard.id || expandedCard.code || expandedCard.name),
                    onClick: (cardWithVariant) => handleAddToWishlist(cardWithVariant, 1)
                } : undefined}
                onCardAdded={(folderId, cardPayload) => {
                    if (folderId === 'wishlist') {
                        const cardKey = String(cardPayload?.id || cardPayload?.code || cardPayload?.name);
                        setWishlistAddedMap(prev => ({ ...prev, [cardKey]: true }));
                    }
                    loadUserData();
                }}
                extraActions={expandedCard ? (cardWithVariant) => {
                    const qty = cardWithVariant?.quantity || expandedCard?.quantity || 1;
                    if (qty <= 1) return null;
                    const cardKey = String(cardWithVariant?.id || cardWithVariant?.code || cardWithVariant?.name);
                    const isAddingAll = addingWishlistKey === `${cardKey}-all`;

                    return (
                        <button
                            type="button"
                            onClick={() => {
                                setAddingWishlistKey(`${cardKey}-all`);
                                handleAddToWishlist(cardWithVariant, qty);
                            }}
                            disabled={isAddingAll || addingWishlistKey === cardKey}
                            className="w-full bg-pink-950/70 hover:bg-pink-900/80 text-pink-200 hover:text-white border border-pink-500/30 hover:border-pink-500/60 font-black uppercase text-[10px] tracking-wider py-2.5 rounded-lg flex items-center justify-center space-x-2 transition-colors active:scale-95 cursor-pointer disabled:opacity-50"
                            title={`Adicionar todas as ${qty} cópias deste deck à sua Wishlist`}
                        >
                            <i className={`fas ${isAddingAll ? 'fa-spinner fa-spin' : 'fa-layer-group'} text-pink-400 text-xs`}></i>
                            <span>Adicionar {qty}x deste Deck à Wishlist</span>
                        </button>
                    );
                } : undefined}
            />
        </div>
    );
};
